import React, { Suspense, lazy } from 'react';
import {
  Controller,
  FormProvider,
  RegisterOptions,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import dayjs from 'dayjs';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit is in the parent dir
import { IJodit } from 'jodit/esm/types';
import { CalendarIcon, ChevronDown, ChevronUpIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import DateTimeInput from 'components/input/DateTimeInput';
import EmailRecipientsInput from 'components/input/EmailInput/EmailRecipientsInput';
import { EmailRecipient } from 'components/input/EmailInput/types';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Divider, Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';
import { PendingEmail } from 'types/PendingOutboxEmail';
import { Maybe } from 'types/UtilityTypes';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { getJoditEditorConfig } from 'utils/getJoditEditorHelpers';
import { cn } from 'utils/shadcn';
import { formatTimestamp } from 'utils/time';

import CancelAndRescheduleEmailButtons from './CancelAndRescheduleEmailButtons';

// Lazy load JoditEditor to reduce initial bundle size and rendering cost
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit-react is in the parent dir
const JoditEditor = lazy(() => import('jodit-react'));

const scheduleSendFieldOptions: Omit<
  RegisterOptions<PendingEmail, 'scheduleSend'>,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
> = {
  validate: (value: Maybe<Date>) => {
    if (value && value < new Date()) {
      return "Drumkit isn't a time machine (yet).... Please select a future date.";
    }

    return true;
  },
};

type CarrierEmailTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & { name: React.ComponentPropsWithoutRef<typeof RHFTextInput>['name'] };
const CarrierEmailTextInput = (props: CarrierEmailTextInputProps) => (
  <RHFTextInput {...props} />
);

interface CarrierEmailItemProps {
  email: PendingEmail;
  timestampOverride?: string;
  isExpanded: boolean;
  isUndoable: boolean;
  loading: boolean;
  normalizedLoad: NormalizedLoad;
  joditRefsRef: React.MutableRefObject<{ [key: number]: Maybe<IJodit> }>;
  onEmailClick: (emailId: number) => void;
  onCancelClick: (emailId: number) => void;
  onUndoCancelClick: (emailId: number) => void;
  onSubmit: SubmitHandler<PendingEmail>;
  toast: (options: {
    description?: string;
    title?: string;
    variant?: 'default' | 'destructive' | 'success';
  }) => void;
}

// Component is wrapped with React.memo to prevent unnecessary re-renders (different from useMemo)
const CarrierEmailItem = React.memo(function CarrierEmailItem({
  email,
  timestampOverride,
  isExpanded,
  isUndoable,
  loading,
  normalizedLoad,
  joditRefsRef,
  onEmailClick,
  onCancelClick,
  onUndoCancelClick,
  onSubmit,
  toast,
}: CarrierEmailItemProps) {
  // Use timestamp override if available (for locally updated timestamps)
  const effectiveTimestamp = timestampOverride || email.timestamp;

  const formMethods = useForm<PendingEmail>({
    defaultValues: {
      ...email,
      cc: (email.cc || []).filter((email) => email && email.trim() !== ''),
    },
  });
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = formMethods;

  const onInvalid: SubmitErrorHandler<PendingEmail> = async () => {
    toast({
      description: 'Some fields are invalid.',
      variant: 'destructive',
    });
  };

  const emailDate = dayjs
    .utc(effectiveTimestamp)
    .local()
    .format('ddd, MMMM Do');

  return (
    <Grid
      className='rounded bg-neutral-50 p-2 mb-3 mx-0 w-full'
      key={email.id}
      data-testid={`outbox-email-item-${email.id}`}
    >
      <Flex
        justify='between'
        align='center'
        onClick={() => {
          onEmailClick(email.id);
        }}
        className={cn(
          'cursor-pointer bg-neutral-200 hover:bg-neutral-100 border border-neutral-400 rounded-sm px-2 py-1.5 -m-2',
          isExpanded && 'bg-neutral-200'
        )}
      >
        <Flex direction='col' gap='none' className='select-none'>
          <Flex direction='row' gap='xs' align='center' className='mt-0.5'>
            <CalendarIcon
              className={cn('h-4 w-4 shrink-0 stroke-neutral-500')}
            />
            <Typography
              variant='body-sm'
              weight='medium'
              className='text-[13px]' // 13px because it looks better (went for midpoint between xs and sm)
              textColor='lessMuted'
            >
              {emailDate},{' '}
              {formatTimestamp(
                dayjs.utc(effectiveTimestamp).local(),
                'HH:mm z'
              )}
            </Typography>
          </Flex>
          <Typography variant='body-xxs' textColor='muted' className='italic'>
            {isExpanded ? 'Click to hide' : 'Click to view or make changes'}
          </Typography>
        </Flex>
        <Typography
          className={cn(
            isExpanded ? 'rotate-180' : '',
            'transition-all duration-300 ease-in-out'
          )}
        >
          <ChevronDown className='h-4 w-4 shrink-0 transition-transform duration-200' />
        </Typography>
      </Flex>

      <div className={cn(isExpanded ? 'block' : 'hidden', '-mx-2')}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <Grid cols='1' gap='sm' className='mt-4'>
              <DateTimeInput<PendingEmail, 'scheduleSend'>
                control={control}
                name='scheduleSend'
                label='Reschedule email'
                options={scheduleSendFieldOptions}
              />

              {/* Cancel and Reschedule email buttons */}
              {!isUndoable ? (
                <CancelAndRescheduleEmailButtons
                  email={email}
                  onCancelClick={onCancelClick}
                  isDirty={isDirty}
                  loading={loading}
                />
              ) : (
                <Button
                  buttonNamePosthog={ButtonNamePosthog.UndoCancelCarrierEmail}
                  type='button'
                  onClick={(e) => {
                    e.preventDefault();
                    onUndoCancelClick(email.id);
                  }}
                  variant='outline'
                  className='w-full text-sm'
                  size='sm'
                >
                  {ButtonText.UndoCancelCarrierEmail}
                </Button>
              )}

              <Divider size='sm' spacing='xs' variant='strong' />

              <Typography variant='h6' weight='semibold'>
                Edit Email
              </Typography>

              <Controller
                name='recipients'
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || value.length === 0) {
                      return 'Recipient is required';
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => {
                  const aiSuggestedRecipients: EmailRecipient[] = normalizedLoad
                    .carrier.email
                    ? [
                        {
                          email: normalizedLoad.carrier.email,
                          name: normalizedLoad.carrier.name || 'Carrier',
                        },
                      ]
                    : [];

                  return (
                    <EmailRecipientsInput
                      label='Recipients'
                      value={field.value || []}
                      onChange={field.onChange}
                      customOptions={aiSuggestedRecipients}
                      placeholder='Type or paste recipient email(s) here'
                      required={true}
                      allowMultiple={true}
                      error={fieldState.error?.message}
                    />
                  );
                }}
              />

              <Controller
                name='cc'
                control={control}
                render={({ field, fieldState }) => (
                  <EmailRecipientsInput
                    name='cc'
                    label='CC'
                    value={(field.value || []).filter(
                      (email) => email && email.trim() !== ''
                    )}
                    onChange={(emails) => {
                      field.onChange(
                        emails.filter((email) => email && email.trim() !== '')
                      );
                    }}
                    placeholder='Type or paste email(s) here'
                    allowMultiple={true}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <CarrierEmailTextInput
                name='subject'
                label='Subject'
                required={true}
              />
            </Grid>
            <Flex direction='col' gap='none' className='w-full mt-3'>
              <Label name='body' required={true}>
                Email Body
              </Label>
              <Controller
                control={control}
                name='body'
                rules={{ required: 'Required' }}
                render={({ field: { onChange, value } }) => (
                  <div className='w-full overflow-hidden'>
                    {/* Optimization #3: Only render JoditEditor when expanded, with Suspense fallback */}
                    {isExpanded && (
                      <Suspense
                        fallback={
                          <div className='h-48 bg-neutral-100 rounded animate-pulse' />
                        }
                      >
                        <JoditEditor
                          value={value}
                          config={getJoditEditorConfig({
                            refToUpdateAfterInit: {
                              get current() {
                                return joditRefsRef.current[email.id] || null;
                              },
                              set current(jodit: Maybe<IJodit>) {
                                joditRefsRef.current[email.id] = jodit;
                              },
                            },
                          })}
                          onBlur={(e: any) => {
                            onChange(e);
                          }}
                        />
                      </Suspense>
                    )}
                  </div>
                )}
              />
              <ErrorMessage
                errors={formMethods.formState.errors}
                name='body'
                render={({ message }: { message: string }) => (
                  <Typography variant='body-xs' className='text-error-500'>
                    {message}
                  </Typography>
                )}
              />
            </Flex>

            {!isUndoable && (
              <Flex direction='col' gap='md' className='mt-4' align='center'>
                <Button
                  buttonNamePosthog={ButtonNamePosthog.UpdateCarrierEmail}
                  type='submit'
                  disabled={!isDirty || loading}
                  className='flex-1 w-full'
                >
                  {loading ? <ButtonLoader /> : ButtonText.UpdateCarrierEmail}
                </Button>
                <Button
                  className='flex items-center gap-1 bg-neutral-100 hover:bg-neutral-200 w-20'
                  type='button'
                  variant='outline'
                  size='xs'
                  onClick={(e) => {
                    e.preventDefault();
                    onEmailClick(email.id);
                  }}
                  buttonNamePosthog={null}
                >
                  <Typography
                    variant='body-sm'
                    className='flex items-center gap-2'
                  >
                    Hide
                    <ChevronUpIcon className='w-4 h-4' />
                  </Typography>
                </Button>
              </Flex>
            )}
          </form>
        </FormProvider>
      </div>
    </Grid>
  );
});

export default CarrierEmailItem;
